import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { appManagementPageQuery, appManagementTableQuery, type AppManagementRow } from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { postKitForm } from './softseeKitForm'
import type { ScreenProps } from './screens'

/**
 * Fila Application Management - applicationManagement.php +
 * application_filters.php + DeviceApplicationsModel. Prima fila e lista
 * instalata (What it is / Decided). Install New si Update deschid
 * ecranele READ deja mutate.
 *
 * Uninstall din coloana Action e formularul `uninstallApplicationForm` si
 * face ce facea `UninstallApplications()` din softsee.js: "Are you sure?",
 * POST pe capatul filei Uninstall Applications, "Uninstall command has been
 * sent." si lista reincarcata. Cancel uninstall vine stins de la server.
 */

const TABLE = 'deviceSoftseeApps'

const HEADS = ['Name', 'Version', 'Platform', 'Category', 'Tag', 'Size', 'Action']

export function DeviceSoftseeApps({ id, screen }: ScreenProps) {
  const query = useQueryClient()
  const page = useQuery(appManagementPageQuery(id))
  const [tab, setTab] = useState('installed')
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)

  const table = useQuery({
    ...appManagementTableQuery(id, { start, length, search, applicationsType: filter }),
    placeholderData: keepPreviousData,
    enabled: !!page.data && tab === 'installed',
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const current = page.data.tabs.find((t) => t.key === tab) ?? page.data.tabs[0]

  return (
    <div className="device-softsee-apps">
      <style>{FILTER_CSS}</style>
      <div className="row">
        <div className="col-md-12">
          <ul className="nav nav-tabs line-tabs mb-2 patchManagementList" role="tablist">
            {page.data.tabs.map((item) => (
              <li key={item.key} className={item.key === tab ? 'active' : undefined}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setTab(item.key)
                  }}
                >
                  <i className={item.icon}></i> {item.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="tab-content">
            {tab === 'installed' ? (
              <InstalledPane
                groups={page.data.groups}
                tags={page.data.tags}
                filter={filter}
                onFilter={(key) => {
                  setFilter(key)
                  setStart(0)
                }}
                rows={table.data?.data ?? []}
                total={table.data?.recordsFiltered ?? 0}
                fetching={table.isFetching}
                error={table.isError ? (table.error as Error).message : null}
                search={search}
                start={start}
                length={length}
                onSearch={(q) => {
                  setSearch(q)
                  setStart(0)
                }}
                onStart={setStart}
                onLength={(n) => {
                  setLength(n)
                  setStart(0)
                }}
                onFormSubmit={async (form) => {
                  if (!form.classList.contains('uninstallApplicationForm')) return
                  if (!confirm('Are you sure?')) return
                  try {
                    const message = await postKitForm(form, id, 'softsee')
                    toast(message, 'Success', 'success')
                    await query.invalidateQueries({ queryKey: ['device', id, 'app-management', 'table'] })
                  } catch {
                    toast('Something went wrong.', 'Error', 'error')
                  }
                }}
              />
            ) : (
              screen(current?.href ?? 'softsee/installApplications/', current?.label ?? 'Install')
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function InstalledPane({
  groups,
  tags,
  filter,
  onFilter,
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  onSearch,
  onStart,
  onLength,
  onFormSubmit,
}: {
  groups: { key: string; label: string }[]
  tags: { key: string; label: string }[]
  filter: string
  onFilter(key: string): void
  rows: AppManagementRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  onFormSubmit(form: HTMLFormElement): void
}) {
  return (
    <>
      <div className="row app-filters">
        <div className="col-md-12">
          <div className="app-filter-row">
            <span className="app-filter-key">What it is</span>
            <span className="app-chips" data-toggle="buttons">
              <Chip label="All" value="all" active={filter === 'all'} onPick={onFilter} />
              {groups.map((g) => (
                <Chip key={g.key} label={g.label} value={g.key} active={filter === g.key} onPick={onFilter} />
              ))}
            </span>
          </div>
          <div className="app-filter-row">
            <span className="app-filter-key">Decided</span>
            <span className="app-chips" data-toggle="buttons">
              {tags.map((t) => (
                <Chip key={t.key} label={t.label} value={t.key} active={filter === t.key} onPick={onFilter} />
              ))}
            </span>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <AppsTable
            rows={rows}
            total={total}
            fetching={fetching}
            error={error}
            search={search}
            start={start}
            length={length}
            onSearch={onSearch}
            onStart={onStart}
            onLength={onLength}
            onFormSubmit={onFormSubmit}
          />
        </div>
      </div>
    </>
  )
}

function Chip({ label, value, active, onPick }: { label: string; value: string; active: boolean; onPick(v: string): void }) {
  return (
    <label className={`btn btn-theme-inverse btn-transparent btn-sm${active ? ' active' : ''}`}>
      <input
        type="radio"
        name="appFilter"
        autoComplete="off"
        value={value}
        checked={active}
        onChange={() => onPick(value)}
      />{' '}
      {label}
    </label>
  )
}

function AppsTable({
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  onSearch,
  onStart,
  onLength,
  onFormSubmit,
}: {
  rows: AppManagementRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  onFormSubmit(form: HTMLFormElement): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)

  useEffect(() => {
    setDraft(search)
  }, [search])
  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${TABLE}_filter`} className="dataTables_filter">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onSearch(value), 400)
                }}
              />
            </label>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table
            className="table table-striped table-hover dataTable applications-table no-footer"
            width="100%"
            id={TABLE}
            role="grid"
            onSubmit={(e) => {
              /* Formularele din coloana Action vin ca HTML de la server; trimiterea lor urca pana aici. */
              e.preventDefault()
              const form = e.target
              if (form instanceof HTMLFormElement) onFormSubmit(form)
            }}
          >
            <thead>
              <tr role="row">
                {HEADS.map((title, i) => (
                  <th
                    key={title}
                    className={`td-small a-left${i === 0 ? ' sorting_asc' : ' sorting'}`}
                    aria-sort={i === 0 ? 'ascending' : undefined}
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={7} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={7} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {row.cells.map((cell, c) => (
                      <td key={c} className="td-small a-left" dangerouslySetInnerHTML={{ __html: cell }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}

const FILTER_CSS = `
    .app-filters { margin-bottom: 14px; }
    .app-filters .app-filter-row { margin-bottom: 8px; }
    .app-filters .app-filter-row:last-child { margin-bottom: 0; }
    .app-filters .app-filter-key {
        display: inline-block;
        width: 74px;
        font-size: 11px;
        letter-spacing: .04em;
        text-transform: uppercase;
        color: #8a94a6;
        vertical-align: middle;
    }
    .app-filters .app-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; vertical-align: middle; }
    .app-filters .app-chips .btn { border-radius: 3px; }
`
